import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
/**
 * Focus Indicator Component
 *
 * Shows whether the AI is querying org-wide or focused on a specific location.
 * Includes dropdown to select a specific location to focus on.
 */
import { cn } from "@plasma/utils/cn";
import { useQuery } from "convex/react";
import { Building2, Check, ChevronDown, MapPin } from "lucide-react";
import { useState } from "react";

import { Button } from "~/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";
import { useActiveOrganization } from "~/integrations/vortex-auth/hooks";

const aiListLocations = api.ai.queries.listLocations;

interface FocusIndicatorProps {
  /** Whether queries are org-wide (all locations) */
  isOrgWide: boolean;
  /** Name of the focused location (if not org-wide) */
  locationName?: string;
  /** Organization name */
  organizationName?: string;
  /** Called when user wants to clear location focus */
  onClearFocus?: () => void;
  /** Called when user selects a location */
  onSelectLocation?: (locationId: Id<"locations">) => void;
  /** Current location ID (for highlighting in dropdown) */
  currentLocationId?: Id<"locations">;
}

export function FocusIndicator({
  isOrgWide,
  locationName,
  onClearFocus,
  onSelectLocation,
  currentLocationId,
}: FocusIndicatorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { organization } = useActiveOrganization();

  // Fetch locations for the dropdown
  const locations = useQuery(
    aiListLocations,
    organization?.id ? { organizationId: organization.id } : "skip"
  );

  const handleSelect = (locationId: Id<"locations"> | null) => {
    if (locationId === null) {
      onClearFocus?.();
    } else {
      onSelectLocation?.(locationId);
    }
    setIsOpen(false);
  };

  return (
    <Popover onOpenChange={setIsOpen} open={isOpen}>
      <PopoverTrigger asChild>
        <Button
          className="h-6 gap-1 px-2 text-xs font-normal"
          size="sm"
          type="button"
          variant="ghost"
        >
          {isOrgWide ? (
            <>
              <Building2 className="h-3 w-3" />
              All Locations
            </>
          ) : (
            <>
              <MapPin className="h-3 w-3" />
              {locationName ?? "Location"}
            </>
          )}
          <ChevronDown className="h-3 w-3 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-56 p-1">
        <div className="text-muted-foreground px-2 py-1.5 text-xs font-medium">
          Query scope
        </div>

        {/* All Locations option */}
        <Button
          className={cn(
            "w-full justify-start gap-2 text-sm font-normal",
            isOrgWide && "bg-accent"
          )}
          onClick={() => handleSelect(null)}
          size="sm"
          type="button"
          variant="ghost"
        >
          <Building2 className="h-4 w-4" />
          <span className="flex-1 text-left">All Locations</span>
          {isOrgWide && <Check className="h-4 w-4" />}
        </Button>

        {/* Divider */}
        {locations && locations.length > 0 && <div className="my-1 border-t" />}

        {/* Location list */}
        {locations?.map((location) => (
          <Button
            className={cn(
              "w-full justify-start gap-2 text-sm font-normal",
              currentLocationId === location.id && "bg-accent"
            )}
            key={location.id}
            onClick={() => handleSelect(location.id)}
            size="sm"
            type="button"
            variant="ghost"
          >
            <MapPin className="h-4 w-4" />
            <span className="flex-1 truncate text-left">{location.name}</span>
            {currentLocationId === location.id && <Check className="h-4 w-4" />}
          </Button>
        ))}

        {/* Empty state */}
        {locations?.length === 0 && (
          <div className="text-muted-foreground px-2 py-3 text-center text-sm">
            No locations found
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
